import {CSSProperties, useEffect, useRef, useState} from 'react';

const SWAP_WINDOW_DURATION_MS = 1000;
const FADE_OUT_MS = 300;
const FADE_IN_MS = 400;
const SWAP_FRAME_MS = 30;
const PRELOAD_TIMEOUT_MS = 2500;

let swapWindowActiveUntil = 0;

/**
 * Opens a time window in which images dissolve into their replacement instead
 * of swapping instantly. Call it right before invalidating requests whose
 * response can change already rendered images, e.g. the product streams
 * refetched after cookie consent, which can return a different image set.
 */
export const markImageSwapTransitionWindow = () => {
  swapWindowActiveUntil = Date.now() + SWAP_WINDOW_DURATION_MS;
};

export const isImageSwapTransitionWindowActive = () =>
  process.browser && Date.now() < swapWindowActiveUntil;

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

interface ImageSource {
  src: string;
  srcSet?: string;
  sizes?: string;
}

interface Transition {
  from: ImageSource;
  to: string;
}

type Phase = 'preload' | 'out' | 'in';

// opacity is animated exclusively: it runs on the compositor and causes no
// layout or paint work on the image itself
const FADE_OUT_STYLE: CSSProperties = {
  opacity: 0,
  transition: `opacity ${FADE_OUT_MS}ms ease-out`,
};

const SWAP_FRAME_STYLE: CSSProperties = {
  opacity: 0,
};

const FADE_IN_STYLE: CSSProperties = {
  opacity: 1,
  transition: `opacity ${FADE_IN_MS}ms ease-in`,
};

/**
 * Dissolves an image into its replacement when `identity` changes while the
 * swap window is active: the old image stays visible until the new one is
 * preloaded, fades out, and the new one fades in. Outside the window (the
 * regular case) the source is passed through untouched, so user driven
 * changes like color variant clicks stay instant.
 */
export const useImageSwapTransition = ({
  identity,
  source,
}: {
  /** What identifies the image, usually the unresolved src prop. */
  identity: string;
  /** The resolved attributes the img is rendered with. */
  source: ImageSource;
}): {source: ImageSource; style?: CSSProperties} => {
  const [transition, setTransition] = useState<Transition | null>(null);
  const [phase, setPhase] = useState<Phase>('preload');
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);

  const lastIdentity = useRef(identity);
  const lastSource = useRef(source);

  if (identity !== lastIdentity.current) {
    lastIdentity.current = identity;
    if (transition) {
      // changed again mid-transition: latest image wins, skip the animation
      setTransition(null);
      setStyle(undefined);
    } else if (
      lastSource.current.src &&
      isImageSwapTransitionWindowActive() &&
      !prefersReducedMotion()
    ) {
      setTransition({from: lastSource.current, to: identity});
      setPhase('preload');
    }
  }

  if (!transition) {
    lastSource.current = source;
  }

  const transitionTarget = transition?.to;
  const {src, srcSet, sizes} = source;

  useEffect(() => {
    if (!transitionTarget) {
      return;
    }

    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const schedule = (fn: () => void, ms: number) =>
      timers.push(setTimeout(fn, ms));

    let fadeStarted = false;
    // The state updates below drive the animation frames of a single transition.
    // The effect only depends on the transition target, so they cannot loop.
    /* eslint-disable local-rules/set-state-in-use-effect */
    const fade = () => {
      if (fadeStarted) {
        return;
      }
      fadeStarted = true;

      setPhase('out');
      setStyle(FADE_OUT_STYLE);
      schedule(() => {
        setPhase('in');
        setStyle(SWAP_FRAME_STYLE);
        schedule(() => {
          setStyle(FADE_IN_STYLE);
          schedule(() => {
            setTransition(null);
            setStyle(undefined);
          }, FADE_IN_MS);
        }, SWAP_FRAME_MS);
      }, FADE_OUT_MS);
    };
    /* eslint-enable local-rules/set-state-in-use-effect */

    // Keep the old image visible until the replacement is loaded, so the
    // dissolve reveals the new image instead of an empty box.
    const preloaded = new window.Image();
    preloaded.onload = fade;
    preloaded.onerror = fade;
    if (sizes) {
      preloaded.sizes = sizes;
    }
    if (srcSet) {
      preloaded.srcset = srcSet;
    }
    preloaded.src = src;
    schedule(fade, PRELOAD_TIMEOUT_MS);

    return () => {
      preloaded.onload = null;
      preloaded.onerror = null;
      timers.forEach(clearTimeout);
    };
    // The sequence runs once per transition; the source values belong to that
    // transition and must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionTarget]);

  if (transition && phase !== 'in') {
    return {source: transition.from, style};
  }

  return {source, style};
};
